import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getAllCategories, getUserById } from '@/lib/community';
import { CURRENT_USER_ID, SITE_NAME } from '@/lib/constants';
import { cn } from '@/lib/format';
import UserAvatar from './UserAvatar';
import SearchOverlay from './SearchOverlay';

const navLinks = [
  { to: '/', label: '首页' },
  { to: '/post', label: '发布话题' },
  { to: '/about', label: '关于我们' },
];

export default function Navbar() {
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const categories = getAllCategories();
  const currentUser = getUserById(CURRENT_USER_ID);

  useEffect(() => {
    setCategoriesOpen(false);
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  const isActive = (to: string) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  return (
    <>
      <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="w-full px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-drop-fill text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-extrabold text-lg text-foreground-900 tracking-tight">{SITE_NAME}</span>
              <span className="hidden sm:block text-[10px] text-foreground-400 font-label tracking-widest mt-0.5">HONEY COMMUNITY</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.slice(0, 1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  'px-4 py-2 rounded-md text-sm font-label font-bold whitespace-nowrap transition-colors cursor-pointer',
                  isActive(link.to) ? 'text-primary-700 bg-primary-100' : 'text-foreground-700 hover:text-primary-700 hover:bg-background-100',
                )}
              >
                {link.label}
              </Link>
            ))}

            {/* Categories dropdown */}
            <div className="relative" onMouseEnter={() => setCategoriesOpen(true)} onMouseLeave={() => setCategoriesOpen(false)}>
              <button
                type="button"
                onClick={() => setCategoriesOpen((v) => !v)}
                className={cn(
                  'px-4 py-2 rounded-md text-sm font-label font-bold whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1',
                  location.pathname.startsWith('/category') ? 'text-primary-700 bg-primary-100' : 'text-foreground-700 hover:text-primary-700 hover:bg-background-100',
                )}
              >
                话题分类
                <i className={cn('ri-arrow-down-s-line transition-transform', categoriesOpen && 'rotate-180')} />
              </button>
              {categoriesOpen && (
                <div className="absolute left-0 top-full pt-2 w-[420px]">
                  <div className="grid grid-cols-2 gap-1.5 p-3 bg-background-50 border border-background-200 rounded-lg shadow-sm">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.id}`}
                        className="flex items-center gap-2.5 p-2.5 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                          <i className={cn(cat.icon, 'text-base')} />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-bold text-foreground-900">{cat.name}</span>
                          <span className="block text-[11px] text-foreground-400 truncate">{cat.topicCount} 个话题</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  'px-4 py-2 rounded-md text-sm font-label font-bold whitespace-nowrap transition-colors cursor-pointer',
                  isActive(link.to) ? 'text-primary-700 bg-primary-100' : 'text-foreground-700 hover:text-primary-700 hover:bg-background-100',
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="搜索"
              className="w-9 h-9 rounded-md border border-background-300 text-foreground-600 flex items-center justify-center hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-search-line text-lg" />
            </button>

            <Link
              to="/post"
              className="hidden md:inline-flex items-center gap-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-4 py-2 whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-add-line" />
              发新帖
            </Link>

            <Link
              to={`/user/${CURRENT_USER_ID}`}
              className="flex items-center gap-2 rounded-full border border-background-300 py-1 pl-1 pr-3 hover:border-primary-400 transition-colors cursor-pointer"
            >
              <UserAvatar src={currentUser?.avatar ?? ''} alt={currentUser?.name ?? '我'} size="sm" />
              <span className="hidden sm:block text-sm font-label font-bold text-foreground-800 max-w-[72px] truncate">
                {currentUser?.name ?? '我的'}
              </span>
            </Link>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="菜单"
              className="md:hidden w-9 h-9 rounded-md border border-background-300 text-foreground-700 flex items-center justify-center cursor-pointer"
            >
              <i className={cn('text-lg', mobileOpen ? 'ri-close-line' : 'ri-menu-line')} />
            </button>
          </div>
        </div>

        {/* Mobile panel */}
        {mobileOpen && (
          <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade-up">
            <div className="flex flex-col gap-1">
              <Link to="/" className="px-3 py-2.5 rounded-md text-sm font-label font-bold text-foreground-800 hover:bg-background-100 cursor-pointer">
                <i className="ri-home-5-line mr-2 text-primary-600" />
                首页
              </Link>
              <Link to={`/user/${CURRENT_USER_ID}`} className="px-3 py-2.5 rounded-md text-sm font-label font-bold text-foreground-800 hover:bg-background-100 cursor-pointer">
                <i className="ri-user-3-line mr-2 text-primary-600" />
                个人主页
              </Link>
              <Link to="/post" className="px-3 py-2.5 rounded-md text-sm font-label font-bold text-foreground-800 hover:bg-background-100 cursor-pointer">
                <i className="ri-add-circle-line mr-2 text-primary-600" />
                发布话题
              </Link>
              <Link to="/about" className="px-3 py-2.5 rounded-md text-sm font-label font-bold text-foreground-800 hover:bg-background-100 cursor-pointer">
                <i className="ri-information-line mr-2 text-primary-600" />
                关于我们
              </Link>
            </div>
            <p className="mt-4 mb-2 px-3 text-xs font-label font-bold text-foreground-400">话题分类</p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-background-100 text-sm font-bold text-foreground-800 cursor-pointer"
                >
                  <i className={cn(cat.icon, 'text-primary-600')} />
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}